import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { RunningBoxIcon, WorldBoxIcon, ParcelCheckIcon, PartnersIcon } from './Icons';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
const AnimatedCounter = ({ value, isVisible }) => {
    const [displayValue, setDisplayValue] = useState(0);
    // Extract numeric part and non-numeric part (suffix)
    // Example: "30k+" -> target: 30, suffix: "k+"
    const match = value.match(/^(\d+)(.*)$/);
    const target = match ? parseInt(match[1], 10) : 0;
    const suffix = match ? match[2] : '';
    useEffect(() => {
        if (!isVisible)
            return;
        let startTimestamp = null;
        const duration = 2000; // 2 seconds animation
        const step = (timestamp) => {
            if (!startTimestamp)
                startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            // Ease out quart function for smooth deceleration
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setDisplayValue(Math.floor(easeOutQuart * target));
            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        };
        window.requestAnimationFrame(step);
    }, [isVisible, target]);
    return (_jsxs("span", { children: [displayValue, suffix] }));
};
export const StatsSection = () => {
    const { elementRef, isVisible } = useScrollAnimation(0.2);
    const stats = [
        {
            value: "100+",
            label: "People working",
            icon: _jsx(RunningBoxIcon, { className: "w-8 h-8 text-red-600" }),
            delay: 0
        },
        {
            value: "180+",
            label: "Countries & territories",
            icon: _jsx(WorldBoxIcon, { className: "w-8 h-8 text-red-600" }),
            delay: 150
        },
        {
            value: "30k+",
            label: "Parcels delivered",
            icon: _jsx(ParcelCheckIcon, { className: "w-8 h-8 text-red-600" }),
            delay: 300
        },
        {
            value: "40+",
            label: "Partners",
            icon: _jsx(PartnersIcon, { className: "w-8 h-8 text-red-600" }),
            delay: 450
        }
    ];
    return (_jsxs("section", { ref: elementRef, className: "py-24 bg-white relative overflow-hidden", children: [_jsx("div", { className: "absolute inset-0 z-0 opacity-[0.03] pointer-events-none bg-[url('https://upload.wikimedia.org/wikipedia/commons/2/2f/World_map_dots_grey.svg')] bg-center bg-no-repeat bg-contain" }), _jsxs("div", { className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10", children: [_jsxs("div", { className: `text-center mb-20 transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`, children: [_jsxs("div", { className: "flex flex-col items-center justify-center mb-6", children: [_jsxs("div", { className: "flex space-x-1 mb-2", children: [_jsx("span", { className: "w-2 h-2 bg-red-600 transform rotate-45" }), _jsx("span", { className: "w-2 h-2 bg-red-600 transform rotate-45" })] }), _jsx("div", { className: "border border-slate-300 px-6 py-2 bg-white/50 backdrop-blur-sm", children: _jsx("span", { className: "text-sm font-bold tracking-[0.2em] text-slate-800 uppercase", children: "We Deliver On Time" }) })] }), _jsx("h2", { className: "text-3xl md:text-5xl font-bold text-slate-900 max-w-2xl mx-auto leading-tight", children: "Total coverage & flexibility in exports & imports" })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8", children: stats.map((item, index) => (_jsxs("div", { className: `flex flex-col items-center justify-center p-8 bg-white border border-slate-100 rounded-lg shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:border-red-100 transition-all duration-500 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`, style: { transitionDelay: `${item.delay}ms` }, children: [_jsx("div", { className: "w-20 h-20 bg-slate-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-red-50 transition-colors", children: item.icon }), _jsx("div", { className: "text-4xl font-extrabold text-slate-900 mb-2", children: _jsx(AnimatedCounter, { value: item.value, isVisible: isVisible }) }), _jsx("div", { className: "text-slate-500 font-medium text-center", children: item.label })] }, index))) })] })] }));
};
